// "Tu producto al centro": los accesos por rubro de la home del teléfono, para todas las cuentas
// (ADR-2026-10-27-los-accesos-por-rubro-para-todas-las-cuentas; nació para el gratis que recién
// entra, ADR-2026-10-23-la-home-del-gratis-nuevo-arranca-por-su-producto). Arriba, chico, el producto
// que cargó en el onboarding y, si ya creó algo, "Ver creaciones"; abajo, los accesos apaisados
// a Modelo, Recrear y Fotos de producto (en Hogar y Decoración, Catálogo de muebles, Recrear
// imagen y Automático), con la portada de cada modo. Modelo, Fotos de producto y Automático abren
// en su paso de elegir productos con ese producto ya elegido (la misma `configRegeneracion` que
// usa la verificación); Recrear, Recrear imagen y Catálogo de muebles abren su primer paso.

import { useNavigate } from 'react-router-dom';
import type { MockProductV2 } from '../mockData';
import type { ConfiguracionDeRegeneracion } from '../verificacion/configurarRegeneracion';
import { useCreacionesRecientes } from '../../../hooks/useCreacionesRecientes';
import { useRubroMarcaActiva } from '../../../hooks/useRubroMarca';
import { caratulaModo } from '../modesCatalog';
import { caratulaConTinte, tieneCaratulaDelRubro } from '../../estilos/seccionesDeModos';
import {
  esRubroBelleza,
  esRubroHogarDeco,
  esRubroModa,
  rubroVePlantillas,
  rubroVePlantillasBelleza,
} from '../../../lib/rubro';

/**
 * El estado que deja el producto ya elegido en el paso de elegir productos del modo, que se
 * muestra igual, con su "atrás" de siempre
 * (ADR-2026-10-27-los-accesos-del-gratis-nuevo-vuelven-a-pasar-por-elegir-producto);
 * todo lo demás como viene.
 */
const conProducto = (
  destino: string,
  varianteId: string | null,
): { configRegeneracion: ConfiguracionDeRegeneracion; primerProductoId: string } | undefined =>
  varianteId
    ? {
        // El paso de elegir productos lo destaca como "Tu primer producto"
        // (ADR-2026-10-27-el-primer-producto-se-destaca-al-entrar-al-modo).
        primerProductoId: varianteId,
        configRegeneracion: {
          destino,
          varianteIds: [varianteId],
          modelo: null,
          modeloGuardadoId: null,
          poseIds: [],
          fondo: null,
          formato: null,
          instruccion: '',
        },
      }
    : undefined;

/**
 * El título de los accesos va cambiando: uno distinto cada vez que se abre la app, en ronda,
 * siempre sobre crear algo nuevo con su producto
 * (ADR-2026-10-27-el-titulo-de-los-accesos-va-cambiando). Cortos, para que entren en una línea.
 */
const TITULOS_DE_LOS_ACCESOS = [
  '¿Qué creamos hoy con tu producto?',
  '¿Cómo mostramos tu producto hoy?',
  'Hagamos algo nuevo con tu producto',
  'Tu producto, en una foto nueva',
  '¿Dónde ponemos tu producto hoy?',
  '¿Qué probamos hoy con tu producto?',
  'Tu próxima foto empieza acá',
  'Dale otra mirada a tu producto',
  '¿Qué imagen nueva hacemos hoy?',
  'Llevá tu producto a otra escena',
];

const CLAVE_TITULO = 'atlas:home-titulo-de-los-accesos';
// Uno por carga de la app y no por montaje: así no cambia al ir y volver de un modo, y el doble
// montaje del StrictMode no saltea títulos.
let tituloDeEstaVisita: string | null = null;
function tituloDeLosAccesos(): string {
  if (tituloDeEstaVisita) return tituloDeEstaVisita;
  let indice = 0;
  try {
    const guardado = localStorage.getItem(CLAVE_TITULO);
    const anterior = guardado === null ? -1 : Number(guardado);
    indice = Number.isInteger(anterior) && anterior >= -1 ? (anterior + 1) % TITULOS_DE_LOS_ACCESOS.length : 0;
    localStorage.setItem(CLAVE_TITULO, String(indice));
  } catch {
    /* sin storage, el primero */
  }
  tituloDeEstaVisita = TITULOS_DE_LOS_ACCESOS[indice];
  return tituloDeEstaVisita;
}

export function HomeTuProducto({
  idCompany,
  idMarca,
  producto,
}: {
  idCompany: string | null;
  idMarca: string | null;
  /** El producto más nuevo de la marca (el del onboarding); `null` si no cargó ninguno. */
  producto: MockProductV2 | null;
}) {
  const navigate = useNavigate();
  // Sólo para saber si ya creó algo (las piezas del onboarding) y ofrecer ir a verlo.
  const { items: creaciones } = useCreacionesRecientes(idCompany, 1, idMarca);
  // La portada de cada modo es la del RUBRO de la marca cuando el rubro tiene la suya —las que
  // muestra el sheet de Generar (`caratulaConTinte`), con productos del rubro— y si no, la del
  // modo (`caratulaModo`). Sin esto Belleza veía ropa en Modelo y Fotos de producto
  // (ADR-2026-10-26-las-portadas-de-la-home-del-gratis-nuevo-son-del-rubro).
  const { rubro, loaded: rubroCargado } = useRubroMarcaActiva();
  const portada = (path: string) => {
    const seccion =
      path === 'modelo' || path === 'fotos-producto' || path === 'catalogo' ? 'catalogo' : 'redes';
    return tieneCaratulaDelRubro(seccion, path, rubro)
      ? (caratulaConTinte(seccion, path, rubro) as string)
      : caratulaModo(path, rubro).poster;
  };
  // "Recrear" es lo que la app ya llama así para el rubro (`modosDeRedesDelRubro`): Plantillas
  // en Moda, las de Belleza en Belleza, y Recrear imagen en los rubros sin plantillas. Ir
  // siempre a Recrear imagen mostraba en Moda la portada de maquillaje, que es la de ese modo
  // (ADR-2026-10-23-recrear-en-la-home-del-gratis-nuevo-es-el-del-rubro).
  const conPlantillas = { bajada: 'Una escena ya armada, con tu producto.' };
  const recrear = rubroVePlantillas(rubro)
    ? { ...conPlantillas, path: 'plantillas', ruta: '/interfaz-v2/plantillas' }
    : rubroVePlantillasBelleza(rubro)
      ? { ...conPlantillas, path: 'plantillas-belleza', ruta: '/interfaz-v2/plantillas-belleza' }
      : { bajada: 'La foto que te gustó, con tu producto.', path: 'replicar-foto', ruta: '/replicar-foto' };

  const modelo = {
    id: 'modelo',
    titulo: 'Modelo',
    bajada: producto || !esRubroModa(rubro) ? 'Tu producto, puesto en una modelo.' : 'Tu prenda, puesta en una modelo.',
    imagen: portada('modelo'),
    abrir: () => navigate('/interfaz-v2/modo/modelo', { state: conProducto('modelo', producto?.id ?? null) }),
  };
  const fotosDeProducto = {
    id: 'fotos-producto',
    titulo: 'Fotos de producto',
    bajada: 'Más fondos y ángulos para tu tienda.',
    imagen: portada('fotos-producto'),
    abrir: () => navigate('/interfaz-v2/modo/fotos-producto', { state: conProducto('fotos-producto', producto?.id ?? null) }),
  };
  // Hogar y Decoración no tiene Modelo ni Fotos de producto (`ocultoEnHogarDeco`): va
  // Automático, con su portada del rubro (ADR-2026-10-26-la-home-del-gratis-nuevo-en-hogar-y-belleza).
  const automatico = {
    id: 'auto',
    titulo: 'Automático',
    bajada: 'Fondo, escena y luz, armados para tu producto.',
    imagen: portada('auto'),
    abrir: () => navigate('/interfaz-v2/modo/auto', { state: conProducto('auto', producto?.id ?? null) }),
  };

  const recrearDelRubro = {
    id: 'recrear',
    titulo: 'Recrear',
    bajada: recrear.bajada,
    imagen: portada(recrear.path),
    // Abre la grilla de plantillas (o Recrear imagen) con su recorrido de siempre: el producto
    // se elige al abrir una plantilla
    // (ADR-2026-10-27-los-accesos-del-gratis-nuevo-vuelven-a-pasar-por-elegir-producto).
    abrir: () => navigate(recrear.ruta),
  };
  // El modo insignia del rubro; abre en su portada, todavía sin el producto
  // (ADR-2026-10-26-la-home-del-gratis-nuevo-en-hogar-y-belleza-2).
  const catalogoDeMuebles = {
    id: 'catalogo-agente',
    titulo: 'Catálogo de muebles',
    bajada: 'Las tomas de tu catálogo, armadas por el asistente.',
    imagen: portada('catalogo-agente'),
    abrir: () => navigate('/catalogo'),
  };

  // Los rubros sin home propia (ni Moda, ni Hogar y Decoración, ni Belleza): Automático,
  // Catálogo y Crear desde cero (ADR-2026-10-27-la-home-del-gratis-nuevo-en-los-otros-rubros).
  // Catálogo lleva por ahora a Generación en lote (`catalogo`), el que hoy anda en cualquier
  // rubro: el Catálogo con el agente (`/catalogo`) todavía es sólo de Hogar y Decoración.
  // Cuando se abra al resto, cambia acá la ruta.
  const catalogo = {
    id: 'catalogo',
    titulo: 'Catálogo',
    bajada: 'Varias tomas de tu producto en una tanda.',
    imagen: portada('catalogo'),
    abrir: () => navigate('/interfaz-v2/modo/catalogo', { state: conProducto('catalogo', producto?.id ?? null) }),
  };
  // "Crear desde cero" es Prompt libre, el sucesor del viejo "Crear desde cero"
  // (ADR-2026-09-13-crear-desde-cero-sale-de-las-listas): describís la imagen y sale.
  const crearDesdeCero = {
    id: 'prompt-libre',
    titulo: 'Crear desde cero',
    bajada: 'Describí la imagen y Atlas la genera.',
    imagen: portada('prompt-libre'),
    abrir: () => navigate('/interfaz-v2/modo/prompt-libre'),
  };

  const accesos = esRubroHogarDeco(rubro)
    ? [catalogoDeMuebles, { ...recrearDelRubro, titulo: 'Recrear imagen' }, automatico]
    : // Sin rubro todavía (la consulta en vuelo, sin cache) van los de siempre: los de otros
      // rubros recién cuando se sabe que no es ninguno de los tres.
      esRubroModa(rubro) || esRubroBelleza(rubro) || (!rubro && !rubroCargado)
      ? [modelo, recrearDelRubro, fotosDeProducto]
      : [automatico, catalogo, crearDesdeCero];

  return (
    // `flex-1` hasta los accesos: se reparten el alto que les deja la sección de la home.
    // `mt-3` + `gap-7`: más aire entre el saludo y cada sección (el título va pegado a lo suyo).
    <div className="mt-3 flex flex-1 flex-col gap-7">
      {producto && (
        <section className="flex flex-col gap-2.5" aria-labelledby="home-tu-producto">
        {/* El título acompaña a la card como el de los accesos (`tituloDeLosAccesos`). */}
        <h2 id="home-tu-producto" className="text-[17px] font-bold tracking-[-0.01em]">
          Tu primer producto
        </h2>
        <div className="flex items-center gap-3 rounded-[20px] bg-white p-2.5 shadow-[0_1px_2px_rgb(var(--brand-black-rgb)_/_0.05),0_8px_24px_rgb(var(--brand-black-rgb)_/_0.06)]">
          <span className="h-14 w-14 shrink-0 overflow-hidden rounded-[14px] bg-[#F5F1EA]">
            {producto.imageUrl && <img src={producto.imageUrl} alt="" className="h-full w-full object-cover" />}
          </span>
          <span className="line-clamp-2 min-w-0 flex-1 text-[15px] font-bold leading-tight tracking-[-0.01em]">
            {producto.name}
          </span>
          {/* Lo que ya creó no se muestra acá: se va a verlo a Creaciones. */}
          {creaciones.length > 0 && (
            <button
              type="button"
              onClick={() => navigate('/redes-sociales/imagenes')}
              className="inline-flex h-9 shrink-0 items-center gap-1 rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.07)] pl-3.5 pr-2.5 text-[13px] font-semibold text-brand-primary active:opacity-70"
            >
              Ver creaciones
              <span aria-hidden="true" className="material-symbols-rounded text-[18px]">
                chevron_right
              </span>
            </button>
          )}
        </div>
        </section>
      )}

      <section className="flex flex-1 flex-col gap-2.5" aria-labelledby="home-que-hacemos">
        <h2 id="home-que-hacemos" className="text-[17px] font-bold tracking-[-0.01em]">
          {tituloDeLosAccesos()}
        </h2>
        {accesos.map((a) => (
          <AccesoDelModo key={a.id} acceso={a} productoUrl={producto?.imageUrl ?? null} />
        ))}
      </section>
    </div>
  );
}

export default HomeTuProducto;

interface Acceso {
  id: string;
  titulo: string;
  bajada: string;
  imagen: string;
  abrir: () => void;
}

const CARD =
  'flex min-h-[156px] w-full flex-1 overflow-hidden rounded-[22px] bg-white text-left shadow-[0_1px_2px_rgb(var(--brand-black-rgb)_/_0.05),0_8px_24px_rgb(var(--brand-black-rgb)_/_0.06)] active:opacity-80';
// Las portadas van con `object-cover`: llenan su lugar y no se ve el fondo propio de cada
// carátula (crema, violeta…), que no coincide entre rubros. Este degradé sólo asoma mientras
// carga la imagen (ADR-2026-10-27-las-portadas-de-los-accesos-llenan-su-lugar).
const FONDO_PORTADA = 'bg-gradient-to-r from-[rgb(var(--brand-primary-rgb)_/_0.13)] to-[rgb(var(--brand-primary-rgb)_/_0.08)]';

function AccesoDelModo({
  acceso,
  productoUrl,
}: {
  acceso: Acceso;
  productoUrl: string | null;
}) {
  // A la izquierda el título, la bajada y "Empezar →"; a la derecha la portada como una foto
  // apoyada, con tu producto montado sobre su borde.
  return (
    <button type="button" onClick={acceso.abrir} className={`${CARD} relative items-stretch`}>
      {/* El texto, hasta el 54% del ancho. La foto mide el alto de la card menos 36px, con el
          ancho topado al 48% y 20px afuera por la derecha: arranca más allá del 54% aunque la
          card crezca, así que nunca pisa el texto
          (ADR-2026-10-27-las-cards-de-los-accesos-tienen-alto-minimo). */}
      <span className="relative z-10 flex min-w-0 max-w-[54%] flex-1 flex-col justify-center gap-2 py-3 pl-5">
        <span className="flex flex-col">
          <span className="text-[18px] font-extrabold leading-[1.1] tracking-[-0.02em]">{acceso.titulo}</span>
          <span className="mt-1 line-clamp-2 text-[12.5px] leading-snug text-black/55">{acceso.bajada}</span>
        </span>
        <span className="inline-flex items-center gap-1 text-[14px] font-bold text-brand-primary">
          Empezar
          <span aria-hidden="true" className="material-symbols-rounded text-[18px]">
            arrow_forward
          </span>
        </span>
      </span>
      {/* La portada como una foto apoyada, igual que las del abanico de las cards del inicio:
          borde blanco, sombra, inclinada y corrida hacia la derecha, donde la card la recorta
          (sin una mitad de corte fijo, ADR-2026-10-27-las-portadas-de-los-accesos-son-fotos-apoyadas).
          Tu producto, montado en su esquina de abajo, como el sello de las de inicio. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-5 top-1/2 h-[calc(100%-36px)] max-w-[48%] aspect-[4/3] -translate-y-1/2 rotate-[5deg]"
      >
        <span className={`block h-full w-full overflow-hidden rounded-[16px] shadow-[0_10px_28px_rgba(16,24,45,0.18)] ring-[3px] ring-white ${FONDO_PORTADA}`}>
          <img src={acceso.imagen} alt="" className="h-full w-full object-cover" />
        </span>
        {productoUrl && (
          <img
            src={productoUrl}
            alt=""
            className="absolute -bottom-2.5 -left-3 h-[52px] w-[52px] -rotate-[5deg] rounded-[12px] bg-white object-cover shadow-[0_4px_10px_rgba(16,24,45,0.18)] ring-[3px] ring-white"
          />
        )}
      </span>
    </button>
  );
}
